import * as XLSX from "xlsx-js-style";

// Cabeceras de columnas que buscamos en el listado importado
const COLUMNS = {
  serial_number: 'S/N',
  imei: 'IMEI',
  mac: 'MAC',
  device_password: 'DevicePassword',
};

// Cuantas filas se miran buscando la de cabeceras antes de rendirse
const HEADER_SEARCH_DEPTH = 10;

const normalizeHeader = (value) => String(value ?? '').trim().toLowerCase();

/**
 * La MAC llega de Teltonika sin separadores: 209727AF5AAA.
 * El resto de la aplicacion la maneja como 20:97:27:AF:5A:AA.
 */
export const normalizeMac = (value) => {
  const clean = String(value ?? '').trim().replace(/[^0-9a-fA-F]/g, '');
  if (clean.length !== 12) return String(value ?? '').trim();
  return clean.toUpperCase().match(/.{2}/g).join(':');
};

/**
 * Busca la fila de cabeceras y devuelve en que columna esta cada campo.
 *
 * @returns {Object|null} { rowIndex, map: { imei: 3, ... }, imeiCount }
 */
const findHeaderRow = (matrix) => {
  const depth = Math.min(HEADER_SEARCH_DEPTH, matrix.length);

  for (let rowIndex = 0; rowIndex < depth; rowIndex++) {
    const row = matrix[rowIndex] ?? [];
    const map = {};
    let imeiCount = 0;

    row.forEach((cell, colIndex) => {
      const header = normalizeHeader(cell);

      Object.entries(COLUMNS).forEach(([field, label]) => {
        if (header !== label.toLowerCase()) return;
        if (field === 'imei') imeiCount++;
        // Nos quedamos con la primera aparicion de cada cabecera
        if (map[field] === undefined) map[field] = colIndex;
      });
    });

    if (map.imei !== undefined) {
      return { rowIndex, map, imeiCount };
    }
  }

  return null;
};

/**
 * Lee un packing list y devuelve las filas en crudo, ya normalizadas.
 *
 * @returns {Object} { rows, error }
 */
export const parseModemWorkbook = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });

  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) {
    return { rows: [], error: 'El fichero no tiene ninguna hoja' };
  }

  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null, raw: false });

  const header = findHeaderRow(matrix);
  if (!header) {
    return { rows: [], error: 'No se ha encontrado la columna IMEI en las primeras filas' };
  }

  if (header.imeiCount > 1) {
    return {
      rows: [],
      error: 'El fichero contiene varios tipos de dispositivo. Pide el listado separado por tipo.',
    };
  }

  const rows = [];

  for (let i = header.rowIndex + 1; i < matrix.length; i++) {
    const row = matrix[i] ?? [];
    const cell = (field) => {
      const index = header.map[field];
      if (index === undefined) return '';
      return String(row[index] ?? '').trim();
    };

    const imei = cell('imei');

    if (!imei) continue;

    rows.push({
      excel_row: i + 1,
      imei,
      serial_number: cell('serial_number'),
      mac: normalizeMac(cell('mac')),
      device_password: cell('device_password'),
    });
  }

  if (rows.length === 0) {
    return { rows: [], error: 'No se ha encontrado ninguna fila con IMEI' };
  }

  return { rows, error: null };
};
